import React, { useEffect, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Upload, Trash2, Save, X } from "lucide-react";
import api from "../../lib/api";

export default function Team() {
  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/team"); setTeam(data); };
  useEffect(() => { load(); }, []);

  const save = async (m) => {
    try {
      if (m.id) await api.patch(`/admin/team/${m.id}`, m);
      else await api.post("/admin/team", m);
      toast.success("Saved"); setEditing(null); await load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Remove this team member?")) return;
    await api.delete(`/admin/team/${id}`); await load();
  };
  const seed = async () => {
    try { const { data } = await api.post("/admin/team/seed"); toast.success(data.seeded ? "Jade + Nassim added" : "Team already populated"); await load(); }
    catch { toast.error("Seed failed"); }
  };

  return (
    <div data-testid="admin-team-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">meet the team</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Team</h1>
          <p className="text-sm text-[#6E6074] mt-1">Names, roles, bios and photos shown on your About page.</p>
        </div>
        <div className="flex gap-2">
          {team.length === 0 && <button onClick={seed} className="sac-btn-secondary" data-testid="team-seed-btn">Seed Jade + Nassim</button>}
          <button onClick={() => setEditing({ name: "", role: "", bio: "", order: team.length })} className="sac-btn-primary" data-testid="team-add-btn"><Plus size={16} /> Add member</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {team.map((m) => (
          <div key={m.id} className="admin-surface p-5" data-testid={`team-row-${m.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-[#F4EDDD] ring-2 ring-[#8EC9C7]/40 shrink-0">
                {m.photo_base64 ? <img src={`data:${m.mime_type||'image/jpeg'};base64,${m.photo_base64}`} alt={m.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center font-display text-2xl text-[#5D2C8F]">{m.name?.[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-[#5D2C8F] truncate">{m.name}</div>
                <div className="font-hand text-[#A685BD]">{m.role}</div>
              </div>
            </div>
            {m.bio && <p className="text-sm text-[#5A4A6B] mt-3 line-clamp-4">{m.bio}</p>}
            <div className="flex items-center gap-3 mt-4 pt-3 border-t border-[#E5E0D8]">
              <button onClick={() => setEditing(m)} className="text-sm text-[#5D2C8F] hover:underline" data-testid={`team-edit-${m.id}`}>Edit</button>
              <button onClick={() => remove(m.id)} className="text-sm text-[#A685BD] ml-auto hover:underline" data-testid={`team-delete-${m.id}`}><Trash2 size={12} className="inline" /> Remove</button>
            </div>
          </div>
        ))}
        {team.length === 0 && <div className="admin-surface p-10 md:col-span-3 text-center text-[#6E6074]">No team members yet — click "Seed Jade + Nassim".</div>}
      </div>

      {editing && <Editor m={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function Editor({ m, onClose, onSave }) {
  const [f, setF] = useState({ id: m.id, name: m.name || "", role: m.role || "", bio: m.bio || "", order: m.order || 0, photo_base64: m.photo_base64 || "", mime_type: m.mime_type || "" });
  const ref = useRef();
  const onPhoto = async (file) => {
    if (!file) return;
    if (!["image/jpeg","image/png","image/webp"].includes(file.type)) { toast.error("JPG/PNG/WebP only"); return; }
    const r = new FileReader();
    r.onload = () => setF({ ...f, mime_type: file.type, photo_base64: r.result.split(",")[1] });
    r.readAsDataURL(file);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="admin-surface w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="team-editor-modal">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display text-2xl text-[#5D2C8F]">{m.id ? "Edit member" : "Add team member"}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-[#FAF6EE] rounded"><X size={18} /></button>
        </div>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-[#F4EDDD] ring-2 ring-[#8EC9C7]/40">
            {f.photo_base64 ? <img src={`data:${f.mime_type||'image/jpeg'};base64,${f.photo_base64}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
          </div>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="team-photo-input" />
          <button onClick={() => ref.current?.click()} className="sac-btn-secondary !py-2 !px-4 text-sm"><Upload size={14} /> Photo</button>
          {f.photo_base64 && <button onClick={() => setF({...f, photo_base64: "", mime_type: ""})} className="text-xs text-[#A685BD]">Remove photo</button>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Name*" v={f.name} on={(v)=>setF({...f,name:v})} testid="team-field-name" />
          <Inp label="Role*" v={f.role} on={(v)=>setF({...f,role:v})} testid="team-field-role" />
          <Inp label="Order" type="number" v={f.order} on={(v)=>setF({...f,order:parseInt(v||0)})} testid="team-field-order" />
          <div className="col-span-2">
            <label className="text-sm text-[#5D2C8F]">Bio</label>
            <textarea rows={5} value={f.bio} onChange={(e)=>setF({...f,bio:e.target.value})} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="team-field-bio" />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]">Cancel</button>
          <button onClick={() => onSave(f)} className="sac-btn-primary" data-testid="team-save-btn"><Save size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, v, on, type="text", testid }) {
  return <div><label className="text-sm text-[#5D2C8F]">{label}</label><input type={type} value={v} onChange={(e)=>on(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid={testid} /></div>;
}
